import { useQuery } from "@blitzjs/rpc"
import { Routes, useParam } from "@blitzjs/next"
import getApplication from "app/applications/queries/getApplication"
import { enabled, Feature } from "app/core/features"
import {
  Component,
  createContext,
  FC,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
} from "react"
import { ApplicationPlatform } from "@superwall/db"
import { SuperwallLastApplicationIdKey } from "./constants"
import { useRouter } from "next/router"

type OnPlatformFunc = (platform: ApplicationPlatform, children: any) => JSX.Element | null

export type ApplicationContextType = {
  isWeb?: boolean
  applicationId: number
  publicApiKey?: string
  featuresEnabled: string[]
  enabled: (feature: Feature) => boolean
  platform?: ApplicationPlatform
  onPlatform: OnPlatformFunc
}

export const ApplicationContext = createContext<ApplicationContextType>({
  isWeb: false,
  applicationId: 0,
  featuresEnabled: [],
  enabled: () => {
    return false
  },
  onPlatform: () => null,
})
export const ApplicationContextProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const router = useRouter()
  // If this is undefined & we have `:applicationId` in the url, then we need to
  // select an application, then redirect
  const applicationIdString = useParam("applicationId", "string")!
  const applicationId = parseInt(applicationIdString, 10)
  const applicationIdIsValid = typeof applicationId === "number" && !isNaN(applicationId)
  const lastApplicationId =
    typeof localStorage != "undefined" ? localStorage.getItem(SuperwallLastApplicationIdKey) : ""
  const [application] = useQuery(
    getApplication,
    { id: applicationId },
    {
      enabled: !!applicationId && applicationIdIsValid,
      suspense: false,
      staleTime: Infinity,
      refetchOnMount: false,
    }
  )

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    const path = window.location.pathname
    if (
      typeof applicationIdString === "undefined" &&
      (path.includes(":applicationId") || path.includes(":app"))
    ) {
      // Redirect to select application page
      // If there's one app, pick it, otherwise show the select app page
      // if (false && lastApplicationId && parseInt(lastApplicationId)) {
      //   const newURL = new URL(window.location.href)
      //   newURL.pathname = newURL.pathname.replace(":applicationId", lastApplicationId)
      //   router.push(newURL.toString())
      // } else {
      // Redirect to select application page
      router.push(
        Routes.SelectApplicationPage({
          pathname: window.location.pathname,
        })
      )
      // }
    }
  }, [applicationIdString, lastApplicationId, router])

  // Write last application to local storage, used in
  // pages/applications/index.tsx
  useEffect(() => {
    try {
      if (applicationId && applicationIdIsValid) {
        localStorage.setItem(SuperwallLastApplicationIdKey, applicationId.toString())
      }
    } catch (error) {}
  }, [applicationId, applicationIdIsValid])

  return (
    <ApplicationContext.Provider
      value={{
        isWeb: application?.platform === ApplicationPlatform.WEB || false,
        applicationId,
        publicApiKey: application?.publicApiKey,
        featuresEnabled: (application?.featuresEnabled as string[]) || [],
        enabled: (feature) => {
          return enabled(feature, application) || false
        },
        platform: application?.platform,
        onPlatform: (platform, children: any) => {
          if (application?.platform === platform) {
            return <>{children}</>
          }
          return null
        },
      }}
    >
      {children}
    </ApplicationContext.Provider>
  )
}

export const useOrganizationId = () => {
  const { applicationId } = useApplicationContext()
  const [application] = useQuery(getApplication, { id: applicationId }, { suspense: false })
  return application?.organizationId
}

export const useDefiniteOrganizationId = () => {
  const organizationId = useOrganizationId()
  if (!organizationId) {
    throw new Error("Organization ID not available")
  }
  return organizationId
}

const Wrapper = ({ children }: PropsWithChildren<{}>) => {
  return <>{children}</>
}
Wrapper.displayName = "Wrapper"

export const whenOrganizationIdAvailable = <T,>(Comp: FunctionComponent<T>) => {
  Comp.displayName = "whenOrganizationIdAvailable"
  // eslint-disable-next-line react/display-name
  return (props: T) => {
    const organizationId = useOrganizationId()
    if (!organizationId) {
      return null
    }
    return (
      <Wrapper>
        {/* @ts-ignore */}
        <Comp {...props} />
      </Wrapper>
    )
  }
}

export const useApplicationContext = () => useContext(ApplicationContext)
